import { useMemo } from 'react';
import { CustomerServiceTag, QuoteApiModel, QuoteOptionApiModel } from '~/lib/api/smartsend/types';
import { P2GGrid } from '@parcel2go/core-ui';
import { P2GBox } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { QuoteTagType } from '../utils/QuoteTagType';
import { P2GDivider } from '@parcel2go/core-ui';
import { P2GLinkGroup } from '@parcel2go/core-ui';
import { P2GLink } from '@parcel2go/core-ui';
import { P2GIcon } from '@parcel2go/core-ui';
import SvgHeart from '../Heart';
import { CurrentSelectionType, FavouriteIncludeServiceTags } from '../utils/CurrentSelectionType';
import { QuoteResult } from '../QuoteResult';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';
import { QuoteLayoutVariant } from './QuoteLayoutVariant';

interface props {
  onSave?: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  quotes: QuoteApiModel[];
  currentSelection: CurrentSelectionType;
  estimatedValue?: number;
  quoteTagType: QuoteTagType;
  updateServiceTags: (add: CustomerServiceTag[], remove: CustomerServiceTag[]) => void;
  serviceTags: CustomerServiceTag[];
  editMode: boolean;
  includeServices: (quotes: QuoteApiModel[]) => void;
  excludeServices: (quotes: QuoteApiModel[]) => void;
  toggleSpecificTag: (tag: CustomerServiceTag) => void;
  maxQuoteOptions: number;
  isXs: boolean;
  children?: React.ReactNode;
  variant: QuoteLayoutVariant;
}

const QuoteIsFavourite = (serviceTags: CustomerServiceTag[], quote: QuoteApiModel) => {
  return serviceTags.some(
    (x) => x.ServiceSlug === quote.Service.Id && FavouriteIncludeServiceTags.every((f) => x.Tags.includes(f))
  );
};

export const QuoteLayoutListView: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const favouriteQuotes = useMemo(
    () => props.quotes.filter((a) => QuoteIsFavourite(props.serviceTags, a)),
    [props.quotes, props.serviceTags]
  );

  const otherQuotes = useMemo(
    () => props.quotes.filter((a) => !QuoteIsFavourite(props.serviceTags, a)),
    [props.quotes, props.serviceTags]
  );

  return (
    <P2GBox pt={3}>
      <P2GGrid container spacing={1} direction="column">
        {props.editMode && (
          <P2GGrid item>
            <P2GBox textAlign="center" pb={1}>
              <P2GLinkGroup
                links={[
                  <P2GLink onClick={() => props.includeServices(props.quotes)}>
                    {t('ADD_BUTTON', { defaultValue: 'Add' })}
                  </P2GLink>,
                  <P2GLink onClick={() => props.excludeServices(props.quotes)}>
                    {t('REMOVE_BUTTON', { defaultValue: 'Remove' })}
                  </P2GLink>
                ]}
              />
            </P2GBox>
          </P2GGrid>
        )}
        {favouriteQuotes.length > 0 && (
          <>
            <P2GGrid item>
              <P2GGrid container alignItems="center" spacing={1}>
                <P2GGrid item>
                  <P2GIcon size="sm" palette="primary">
                    <SvgHeart />
                  </P2GIcon>
                </P2GGrid>
                <P2GGrid item>
                  <P2GText variant="h3">
                    <Trans
                      i18nKey="QUOTE_FAVOURITES"
                      t={t}
                      defaults="Favourites <small>({{count}} Selected)</small>"
                      count={favouriteQuotes.length}
                      components={{
                        small: <P2GText small />
                      }}
                    />
                  </P2GText>
                </P2GGrid>
              </P2GGrid>
            </P2GGrid>
            <P2GGrid item>
              <P2GDivider />
            </P2GGrid>
            {favouriteQuotes.map((q, i) => (
              <P2GGrid item key={q.Service.Id}>
                <div data-id={q.Service.Id}>
                  <QuoteResult
                    variant={props.variant}
                    isXs={props.isXs}
                    maxQuoteOptions={props.maxQuoteOptions}
                    toggleSpecificTag={props.toggleSpecificTag}
                    index={i}
                    key={q.Service.Id}
                    updateServiceTags={props.updateServiceTags}
                    quote={q}
                    estimatedValue={props.estimatedValue}
                    onSave={props.onSave}
                    currentSelection={props.currentSelection}
                    serviceTags={props.serviceTags.find((x) => x.ServiceSlug === q.Service.Id)?.Tags || []}
                    editMode={props.quoteTagType}
                  />
                </div>
              </P2GGrid>
            ))}
            <P2GGrid item>
              <P2GBox pt={3} pb={3}>
                <P2GDivider />
              </P2GBox>
            </P2GGrid>
          </>
        )}
        {otherQuotes.map((q, i) => (
          <P2GGrid item key={q.Service.Id}>
            <div data-id={q.Service.Id}>
              <QuoteResult
                variant={props.variant}
                isXs={props.isXs}
                toggleSpecificTag={props.toggleSpecificTag}
                index={i}
                key={q.Service.Id}
                updateServiceTags={props.updateServiceTags}
                quote={q}
                estimatedValue={props.estimatedValue}
                onSave={props.onSave}
                currentSelection={props.currentSelection}
                serviceTags={props.serviceTags.find((x) => x.ServiceSlug === q.Service.Id)?.Tags || []}
                editMode={props.quoteTagType}
                maxQuoteOptions={props.maxQuoteOptions}
              />
            </div>
          </P2GGrid>
        ))}
      </P2GGrid>
    </P2GBox>
  );
};
